import {
  PACKAGE_TIER_KEYS,
  PLATFORM_VERTICAL_KEYS,
  REGISTRY_BLOCK,
  THEME_ID,
  type PlatformVerticalKey,
  type ThemeId,
} from '../catalog/platformCatalogIds';
import { THEME_REGISTRY } from '../theme/themeCatalog';
import type { VerticalDefinition } from './types';

export type { TierBlockConfiguration, VerticalDefinition } from './types';

const T = REGISTRY_BLOCK.type;

export const PLATFORM_VERTICAL_MATRIX: Record<PlatformVerticalKey, VerticalDefinition> = {
  [PLATFORM_VERTICAL_KEYS.restaurant]: {
    label: 'Restaurant',
    description:
      'Perfect for fine dining, sushi counters, bistros, and local eateries looking to showcase menus and secure seamless table reservations.',
    defaultTheme: THEME_ID.michelinDark,
    themes: [
      { id: THEME_ID.michelinDark, name: THEME_REGISTRY[THEME_ID.michelinDark].name },
      { id: THEME_ID.limestoneOrangery, name: THEME_REGISTRY[THEME_ID.limestoneOrangery].name },
      { id: THEME_ID.moorlandHearth, name: THEME_REGISTRY[THEME_ID.moorlandHearth].name },
      { id: THEME_ID.bistroBrasserie, name: THEME_REGISTRY[THEME_ID.bistroBrasserie].name },
      { id: THEME_ID.hinokiCounter, name: THEME_REGISTRY[THEME_ID.hinokiCounter].name },
      { id: THEME_ID.lotusSteam, name: THEME_REGISTRY[THEME_ID.lotusSteam].name },
      { id: THEME_ID.tileAwning, name: THEME_REGISTRY[THEME_ID.tileAwning].name },
      { id: THEME_ID.lanternCourt, name: THEME_REGISTRY[THEME_ID.lanternCourt].name },
      { id: THEME_ID.boldStreet, name: THEME_REGISTRY[THEME_ID.boldStreet].name },
      { id: THEME_ID.mustardRail, name: THEME_REGISTRY[THEME_ID.mustardRail].name },
      { id: THEME_ID.tahiniWindow, name: THEME_REGISTRY[THEME_ID.tahiniWindow].name },
    ],
    tiers: {
      [PACKAGE_TIER_KEYS.minimum]: {
        label: 'Essential Presence',
        description:
          'A clean, single-page online home showing your location, opening hours, and a beautiful static text menu.',
        allowedBlocks: [T.navigation, T.hero, T.staticMenu, T.footer],
      },
      [PACKAGE_TIER_KEYS.recommended]: {
        label: 'Growth Engine',
        description:
          'Our most popular choice. Unlocks active reservation forms, interactive menus with category tabs, and deep storytelling sections.',
        allowedBlocks: [
          T.navigation,
          T.hero,
          T.staticMenu,
          T.dynamicMenu,
          T.chefStory,
          T.kitchenTeam,
          T.imageGallery,
          T.reservation,
          T.contact,
          T.contactChannels,
          T.backToTop,
          T.announcementBar,
          T.utilityBar,
          T.footer,
        ],
      },
      [PACKAGE_TIER_KEYS.plus]: {
        label: 'Premium Experience',
        description:
          'Built for high-end operations. Includes immersive video headers, advanced allergy capture forms, and an elegant multi-course timeline.',
        allowedBlocks: [
          T.navigation,
          T.hero,
          T.staticMenu,
          T.dynamicMenu,
          T.chefStory,
          T.kitchenTeam,
          T.imageGallery,
          T.reservation,
          T.tastingTimeline,
          T.allergyForm,
          T.drinksList,
          T.privatePartyInquiry,
          T.contact,
          T.contactChannels,
          T.backToTop,
          T.announcementBar,
          T.utilityBar,
          T.footer,
        ],
        defaultStarterBlocks: [
          T.navigation,
          T.hero,
          T.staticMenu,
          T.dynamicMenu,
          T.chefStory,
          T.footer,
        ],
        optionalAddonBlocks: [
          T.reservation,
          T.tastingTimeline,
          T.allergyForm,
          T.drinksList,
          T.privatePartyInquiry,
        ],
      },
    },
  },
  [PLATFORM_VERTICAL_KEYS.cafe]: {
    label: 'Cafe & Bakery',
    description:
      'Tailored for coffee shops, artisan bakeries, and pastry spots requiring fast mobile loading and real-time stock availability updates.',
    defaultTheme: THEME_ID.brutalistGrind,
    themes: [
      { id: THEME_ID.brutalistGrind, name: THEME_REGISTRY[THEME_ID.brutalistGrind].name },
      { id: THEME_ID.earthClay, name: THEME_REGISTRY[THEME_ID.earthClay].name },
    ],
    tiers: {
      [PACKAGE_TIER_KEYS.minimum]: {
        label: 'Quick Start',
        description:
          'A fast-loading mobile layout featuring your standard drink list, map, and hours of operation.',
        allowedBlocks: [T.navigation, T.hero, T.openingHours, T.addressMap, T.footer],
      },
      [PACKAGE_TIER_KEYS.recommended]: {
        label: 'Active Cafe',
        description:
          'Engage regulars with active "Sold Out" status badges for your morning pastry bakes and direct social media integrations.',
        allowedBlocks: [
          T.navigation,
          T.hero,
          T.openingHours,
          T.addressMap,
          T.liveBakeryCounter,
          T.socialFeed,
          T.imageGallery,
          T.contact,
          T.contactChannels,
          T.backToTop,
          T.announcementBar,
          T.utilityBar,
          T.footer,
        ],
      },
      [PACKAGE_TIER_KEYS.plus]: {
        label: 'Full Roastery',
        description:
          'Scale your revenue globally by accepting local pre-orders and launching monthly coffee bean subscription deliveries.',
        allowedBlocks: [
          T.navigation,
          T.hero,
          T.openingHours,
          T.addressMap,
          T.liveBakeryCounter,
          T.socialFeed,
          T.imageGallery,
          T.coffeeSubscription,
          T.preOrderPickup,
          T.contact,
          T.contactChannels,
          T.backToTop,
          T.announcementBar,
          T.utilityBar,
          T.footer,
        ],
      },
    },
  },
  [PLATFORM_VERTICAL_KEYS.bar]: {
    label: 'Bar & Lounge',
    description:
      'Designed for cocktail lounges, craft taprooms, and wine bars focused on high-end visual atmosphere and private event bookings.',
    defaultTheme: THEME_ID.neonNoir,
    themes: [
      { id: THEME_ID.neonNoir, name: THEME_REGISTRY[THEME_ID.neonNoir].name },
      { id: THEME_ID.velvetVintage, name: THEME_REGISTRY[THEME_ID.velvetVintage].name },
    ],
    tiers: {
      [PACKAGE_TIER_KEYS.minimum]: {
        label: 'Base Menu',
        description: 'Establish your brand online with a sleek drinks list and basic contact features.',
        allowedBlocks: [T.navigation, T.hero, T.drinksList, T.footer],
      },
      [PACKAGE_TIER_KEYS.recommended]: {
        label: 'Lounge Standard',
        description:
          'Unlocks dynamic cocktail spotlights detailing premium ingredients alongside full private party booking inquiry forms.',
        allowedBlocks: [
          T.navigation,
          T.hero,
          T.drinksList,
          T.cocktailSpotlight,
          T.privatePartyInquiry,
          T.imageGallery,
          T.contact,
          T.contactChannels,
          T.backToTop,
          T.announcementBar,
          T.utilityBar,
          T.footer,
        ],
      },
      [PACKAGE_TIER_KEYS.plus]: {
        label: 'High Voltage',
        description:
          'Maximum performance. Displays automated live tap syncs, background video loops, and a dedicated VIP table booking manager.',
        allowedBlocks: [
          T.navigation,
          T.hero,
          T.drinksList,
          T.cocktailSpotlight,
          T.privatePartyInquiry,
          T.imageGallery,
          T.liveTapList,
          T.vipTableBooking,
          T.contact,
          T.contactChannels,
          T.backToTop,
          T.announcementBar,
          T.utilityBar,
          T.footer,
        ],
      },
    },
  },
  [PLATFORM_VERTICAL_KEYS.foodTruck]: {
    label: 'Food Truck & Pop-Up',
    description:
      'Engineered for mobile kitchens, pop-up dinner experiences, and catering companies where location tracking is vital.',
    defaultTheme: THEME_ID.boldStreet,
    themes: [
      { id: THEME_ID.boldStreet, name: THEME_REGISTRY[THEME_ID.boldStreet].name },
      { id: THEME_ID.cleanNomad, name: THEME_REGISTRY[THEME_ID.cleanNomad].name },
    ],
    tiers: {
      [PACKAGE_TIER_KEYS.minimum]: {
        label: 'Street Basics',
        description: 'Tell guests where you are today, what you serve, and how to reach you.',
        allowedBlocks: [T.navigation, T.hero, T.staticLocation, T.simpleMenuStrip, T.footer],
      },
      [PACKAGE_TIER_KEYS.recommended]: {
        label: 'Rolling Kitchen',
        description: 'Weekly route planner plus catering inquiry forms for events and corporate bookings.',
        allowedBlocks: [
          T.navigation,
          T.hero,
          T.staticLocation,
          T.simpleMenuStrip,
          T.weeklySchedule,
          T.cateringInquiry,
          T.faq,
          T.imageGallery,
          T.contact,
          T.contactChannels,
          T.backToTop,
          T.announcementBar,
          T.utilityBar,
          T.footer,
        ],
      },
      [PACKAGE_TIER_KEYS.plus]: {
        label: 'Pop-Up Pro',
        description: 'Live GPS embed, ticketed pop-up sales, and full mobile conversion tooling.',
        allowedBlocks: [
          T.navigation,
          T.hero,
          T.staticLocation,
          T.simpleMenuStrip,
          T.weeklySchedule,
          T.cateringInquiry,
          T.faq,
          T.imageGallery,
          T.liveGps,
          T.popUpTicket,
          T.contact,
          T.contactChannels,
          T.backToTop,
          T.announcementBar,
          T.utilityBar,
          T.footer,
        ],
      },
    },
  },
};

export function resolveVerticalDefaultTheme(vertical: string): ThemeId | undefined {
  return PLATFORM_VERTICAL_MATRIX[vertical as PlatformVerticalKey]?.defaultTheme;
}

export function getVerticalDefinition(vertical: string): VerticalDefinition | undefined {
  return PLATFORM_VERTICAL_MATRIX[vertical as PlatformVerticalKey];
}
